import * as React from 'react';
import { cn } from '../lib/cn.js';

/**
 * The brand mark and its lockup.
 *
 * Geometry lives here and nowhere else: `docs/design/brand/` holds the same two
 * paths as standalone files for anything outside the app (favicons, a slide),
 * and the source boards they were measured off.
 *
 * The mark paints `currentColor` through a mask, so it is a piece of text as
 * far as colour is concerned — it follows `--accent` into dark mode and drops
 * onto any ground without a second file. The wordmark is live Newsreader, not
 * outlines: it inherits the type scale and stays selectable.
 */

/** Straight sides capped by a true semicircle, r = half the width. */
const ARCH = 'M0 107.3V50A50 50 0 0 1 100 50V107.3Z';

/** The doorway, knocked out of the arch. Runs past it — the mask clips. */
const CUT =
  'M-9.6 15.2L54.4 38.7L31.8 48.7L31.6 90L-9.6 109.6L-9.6 116.1L39.8 92L57.9 92L57.9 116.9L63.2 116.9L63.2 34.9L-9.6 8.7Z';

/** The same cut offset outward, floor block dropped. See `compact` below. */
const CUT_COMPACT =
  'M-12 16.9L48.1 38.9L30 46.9L29.2 88.6L-12 108.1L-12 127L65.1 127L65.1 33.1L-12 5.4Z';

export interface LogoMarkProps extends React.ComponentProps<'svg'> {
  /**
   * The small-size cut: wider bands, no floor block. Use it below roughly
   * 32px, where the full mark's doorway closes up and the shape reads as a
   * blob. Above that the full mark is the one.
   */
  compact?: boolean;
}

export function LogoMark({ compact = false, className, ...props }: LogoMarkProps) {
  // `useId` yields colons, which are legal in an id but break `url(#…)`.
  const maskId = `logo-mark-${React.useId().replace(/:/g, '')}`;
  // Hidden from assistive tech means hidden: a labelled `img` role would still
  // be announced, so the lockup would say the product name twice.
  const decorative = props['aria-hidden'] === true || props['aria-hidden'] === 'true';

  return (
    <svg
      data-slot="logo-mark"
      viewBox="0 0 100 107.3"
      role={decorative ? 'presentation' : 'img'}
      aria-label={decorative ? undefined : 'AImmo'}
      className={cn('h-6 w-auto text-accent', className)}
      {...props}
    >
      <mask id={maskId} maskUnits="userSpaceOnUse" x="-20" y="-20" width="140" height="160">
        <path d={ARCH} fill="#fff" />
        <path d={compact ? CUT_COMPACT : CUT} fill="#000" />
      </mask>
      <rect width="100" height="107.3" fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
}

export interface LogoLockupProps extends React.ComponentProps<'div'> {
  /** Show the payoff under the wordmark. Off in the app chrome, which the user
   *  is already inside. There is no sign-in screen to put it on — that page is
   *  Keycloak's, reached by redirect — so this is for a PWA splash, an export
   *  header, or a Keycloak realm theme if one is ever built. */
  tagline?: boolean;
}

export function LogoLockup({ tagline = false, className, ...props }: LogoLockupProps) {
  return (
    <span
      data-slot="logo-lockup"
      className={cn('inline-flex items-center gap-2.5', className)}
      {...props}
    >
      {/* The wordmark next to it already says "AImmo"; one name per lockup. */}
      <LogoMark aria-hidden="true" className={tagline ? 'h-9' : 'h-7'} />
      <span className="flex flex-col gap-0.5">
        <span className="font-display text-section leading-none text-ink">AImmo</span>
        {tagline ? (
          <span className="text-label uppercase tracking-[0.08em] text-muted">
            Gestisci. Organizza. Concludi.
          </span>
        ) : null}
      </span>
    </span>
  );
}
